<template>
  <div ref="wrapperRef" class="country-dropdown-wrapper d-inline-flex bg-white position-relative">
    <VMenu
      transition="scale-transition"
      location="top"
      :attach="wrapperRef"
      class="country-dropdown"
      content-class="country-dropdown-content"
    >
      <template #activator="{ props }">
        <SBtn
          text
          class="pa-2 pl-4 ma-0 country-dropdown-btn"
          v-bind="props"
          min-width="200"
        >
          <Icon
            :type="currentCountry.icon"
            aspect="four-by-three"
            size="md"
            class="flag-icon"
          />
          <span class="px-2 country-name font-weight-medium">
            {{ countryName }}
          </span>
          <template #append>
            <VIcon class="chevron-icon">
              {{ icons.mdiMenuDown }}
            </VIcon>
          </template>
        </SBtn>
      </template>
    <VList
      v-for="region in sortedRegions"
      :key="region.name"
      class="pa-0"
      elevation="0"
      rounded="0"
    >
      <VListSubheader
        class="font14 font-weight-bold text-primary region-header"
      >
        {{ region.name }}
      </VListSubheader>
      <VDivider />
      <template v-for="(country, index) in region.countries">
        <VListItem
          v-if="country.active"
          :key="index"
          :ripple="false"
          class="pl-2 pr-4 selectable country-item"
          role="link"
          :aria-label="getCountryMenuName(country)"
          @click="changeLocale(country)"
        >
          <VRow no-gutters align="center">
            <Icon
              :type="country.icon"
              size="lg"
              aspect="four-by-three"
              class="ml-2 mr-3 flag-icon"
            />
            <span class="text-primary font12">
              {{ getCountryMenuName(country) }}
            </span>
          </VRow>
        </VListItem>
      </template>
    </VList>
    </VMenu>
    <ConfirmationDialog
      v-model:show-dialog="showDialog"
      :action-function="changeAuthenticatedLocale"
      :title="$t('country.confirmation.title')"
      :text="$t('country.confirmation.text')"
      :cancel-text="$t('input.btn.cancel')"
      :confirm-text="$t('btn.confirm')"
    />
  </div>
</template>

<script lang="ts" setup>
import { mdiMenuDown } from '@mdi/js'
import Icon from '~/components/base/StIcon.vue'
import ConfirmationDialog from '~/components/base/ConfirmationDialog.vue'
import type { Country, Culture } from '~/types/locale'
import { UserRole } from '~/types/account'
import { useLocaleStore } from '~/stores/locale'

const icons = { mdiMenuDown }
const showDialog = ref(false)
const culture = ref<Culture>()
const wrapperRef = ref<HTMLElement>()

const { user } = useSAuth()
const $i18n = useI18n()
const route = useRoute()
const localeStore = useLocaleStore()

const currentCountry = computed(() => localeStore.country())
const countryName = computed(() => $i18n.t(`country-${currentCountry.value.countryCode}`))
const sortedRegions = computed(() => localeStore.sortedRegions)

const changeLocale = async (country: Country) => {
  if (user.value?.culture !== country.culture) {
    culture.value = country.culture
    if (user.value?.role === UserRole.Anonymous) {
      const joinPage = route.name === 'join' || route.name === 'lang-join'
      const routeToKeep = joinPage ? 'join' : ''
      await localeStore.changeLocale({ demoCulture: culture.value, routeToKeep })
    } else {
      showDialog.value = true
    }
  }
}

const changeAuthenticatedLocale = async () => {
  showDialog.value = false
  await localeStore.changeAuthenticatedLocale({
    culture: culture.value!
  })
}

const getCountryMenuName = (country: Country) => {
  // nuxt2 labels the list by culture ("Canada (EN)" / "Canada (FR)"), so multi-
  // language markets are told apart, visually and by their accessible names. AB#63678
  return $i18n.t(`country-menu-${country.culture}`)
}
</script>

<style lang="scss" scoped>
.selectable {
  cursor: pointer;
}

.flag-icon {
  width: 1.5em;
  height: 1.5em;
}

.country-name {
  color: rgb(var(--v-theme-primary));
  text-transform: none;
}

.chevron-icon {
  color: rgb(var(--v-theme-primary));
  font-size: 18px;
}

.region-header {
  height: 36px;
  min-height: 36px;
}

.country-item {
  min-height: 36px;
  height: 36px;
}

.country-dropdown-btn {
  border: 1px solid rgb(var(--v-theme-border));
  border-radius: 0;
}
</style>

<style lang="scss">
// Non-scoped - required for VMenu content which renders in a portal
@layer app-trumps {
  .country-dropdown-content {
    background: white;
    box-shadow: none;
    border: 1px solid rgb(var(--v-theme-border));
    border-radius: 0;
  }
}
</style>
